import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";

interface CanonicalEvent {
  id: number;
  event_date: string | null;
  event_datetime: string | null;
  date_precision: string;
  headline: string;
  description: string | null;
  actors: { name: string; role?: string }[] | null;
  source_span: string | null;
  provenance_token: string;
  classification: string;
  confidence: string | number;
  document_id?: number | null;
  document_name?: string | null;
  document_uploaded_at?: string | null;
}

/** Sentinel for the "chat-saved / no source document" option in the doc filter. */
const NO_DOC = -1;

type ViewMode = "list" | "timeline";

interface ChunkResolution {
  provenance_token: string;
  source_type?: string | null;
  modality?: string | null;
  document_id: number | null;
  video_id?: number | null;
  document_name: string;
  mime_type?: string | null;
  page?: number | null;
  paragraph?: number | null;
  chunk_index?: number | null;
  start_seconds?: number | null;
  end_seconds?: number | null;
  speaker_label?: string | null;
  text: string;
  document_deleted?: boolean;
}

const CLASSIFICATIONS = [
  { value: "VERIFIED_FACT", label: "verified fact" },
  { value: "SUPPORTED_INFERENCE", label: "supported inference" },
  { value: "IDENTIFIED_GAP", label: "identified gap" },
] as const;

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250] as const;

export default function TimelineView({
  caseId,
  refreshKey = 0,
}: {
  caseId: number;
  refreshKey?: number;
}) {
  const [events, setEvents] = useState<CanonicalEvent[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // ---- view mode ----
  const [view, setView] = useState<ViewMode>("list");

  // ---- filter state ----
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selectedClasses, setSelectedClasses] = useState<Set<string>>(new Set());
  const [selectedActors, setSelectedActors] = useState<Set<string>>(new Set());
  // Document filter: "all" | document id | NO_DOC (chat-saved, no document).
  const [selectedDoc, setSelectedDoc] = useState<number | "all">("all");

  // ---- pagination ----
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState<number>(50);
  // While true, render the full filtered list (not just the current page)
  // so "Export PDF" produces the entire chronology, not just one screen.
  const [printingAll, setPrintingAll] = useState(false);

  // ---- modal state (single open event at a time) ----
  const [activeEvent, setActiveEvent] = useState<CanonicalEvent | null>(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const data = await api.get<CanonicalEvent[]>(`/cases/${caseId}/timeline`);
        if (!cancel) setEvents(data);
      } catch (e: any) {
        if (!cancel) setErr(e?.message ?? "Failed to load timeline");
      }
    })();
    return () => { cancel = true; };
  }, [caseId, refreshKey]);

  const allDocs = useMemo(() => {
    if (!events) return [] as { id: number; name: string }[];
    const byId = new Map<number, string>();
    for (const e of events) {
      if (e.document_id != null) {
        byId.set(e.document_id, e.document_name || `Document ${e.document_id}`);
      }
    }
    return Array.from(byId.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [events]);

  // True if any event came from chat/manual save (no source document).
  const hasChatSaved = useMemo(
    () => !!events?.some((e) => e.document_id == null),
    [events],
  );

  const allActors = useMemo(() => {
    if (!events) return [] as string[];
    const seen = new Set<string>();
    for (const e of events) {
      for (const a of e.actors ?? []) {
        const n = (a?.name ?? "").trim();
        if (n) seen.add(n);
      }
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [events]);

  const filtered = useMemo(() => {
    if (!events) return [] as CanonicalEvent[];
    const needle = search.trim().toLowerCase();
    return events.filter((e) => {
      if (selectedDoc !== "all") {
        if (selectedDoc === NO_DOC) {
          if (e.document_id != null) return false;
        } else if (e.document_id !== selectedDoc) {
          return false;
        }
      }
      if (selectedClasses.size > 0 && !selectedClasses.has(e.classification)) return false;
      if (selectedActors.size > 0) {
        const names = (e.actors ?? []).map((a) => (a?.name ?? "").trim());
        const hit = names.some((n) => selectedActors.has(n));
        if (!hit) return false;
      }
      // Filter on the *displayed* date (event date if present, else doc
      // upload-date fallback) so the From/To range matches what the user sees.
      const iso = eventDateView(e).iso;
      if (from && (!iso || iso < from)) return false;
      if (to && (!iso || iso > to)) return false;
      if (needle) {
        const hay = [
          e.headline,
          e.description ?? "",
          e.source_span ?? "",
          (e.actors ?? []).map((a) => `${a?.name ?? ""} ${a?.role ?? ""}`).join(" "),
        ].join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [events, search, from, to, selectedClasses, selectedActors, selectedDoc]);

  // Any filter or page-size change resets to page 1 — otherwise the user
  // could land on an empty page after narrowing the filter set.
  useEffect(() => {
    setOffset(0);
  }, [search, from, to, selectedClasses, selectedActors, selectedDoc, pageSize]);

  // Slice the filtered list down to the current page. CSV / PDF export
  // intentionally still operate on `filtered` so "export" means "everything
  // matching my filters", not just what's on screen.
  const total = filtered.length;
  const paged = useMemo(
    () => filtered.slice(offset, offset + pageSize),
    [filtered, offset, pageSize],
  );
  // What the list/chrono actually renders. Equal to `paged` on screen and
  // `filtered` while we're snapshotting the DOM for print.
  const displayed = printingAll ? filtered : paged;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pageNum = Math.floor(offset / pageSize) + 1;
  const pageStartHuman = total === 0 ? 0 : offset + 1;
  const pageEndHuman = Math.min(offset + paged.length, total);
  const hasPrev = offset > 0;
  const hasNext = offset + pageSize < total;

  function toggleSet(set: Set<string>, value: string, setter: (s: Set<string>) => void) {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    setter(next);
  }

  function clearFilters() {
    setSearch(""); setFrom(""); setTo("");
    setSelectedClasses(new Set()); setSelectedActors(new Set());
    setSelectedDoc("all");
  }

  function exportCsv() {
    const rows = [
      ["date", "precision", "classification", "confidence", "headline", "description", "actors", "source_span", "provenance_token"],
      ...filtered.map((e) => [
        formatDate(e.event_date, e.event_datetime, e.date_precision),
        e.date_precision,
        e.classification,
        String(e.confidence ?? ""),
        e.headline,
        e.description ?? "",
        (e.actors ?? []).map((a) => `${a.name}${a.role ? ` (${a.role})` : ""}`).join("; "),
        e.source_span ?? "",
        e.provenance_token,
      ]),
    ];
    const csv = rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `case-${caseId}-timeline-${todayStamp()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5_000);
  }

  function exportPdf() {
    // The print stylesheet was tailored to the list layout (per-row hidden
    // detail block expanded for paper). Force the list view before printing
    // so a user printing while in "Timeline" view still gets the polished
    // chronology rather than a screenshot of the visual rail. Also expand
    // pagination for the print snapshot so the user gets ALL filtered
    // events, not just the current screen page.
    const prevView = view;
    if (prevView !== "list") setView("list");
    setPrintingAll(true);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      window.print();
      setPrintingAll(false);
      if (prevView !== "list") setView(prevView);
    }));
  }

  if (err) return <div className="alert alert--error">{err}</div>;
  if (!events) return <div className="muted">Loading timeline…</div>;
  if (events.length === 0) {
    return (
      <div className="empty">
        <p>
          No timeline events yet. Open a document in the <strong>Documents</strong> tab and
          click <strong>Build timeline</strong> to extract a cited chronology of dated events
          from it. Events you save from chat answers also appear here.
        </p>
      </div>
    );
  }

  const filtersActive = !!(search || from || to || selectedClasses.size || selectedActors.size || selectedDoc !== "all");

  return (
    <div className="timeline-page">
      <div className="timeline-toolbar no-print">
        <div className="timeline-toolbar__row">
          <input
            type="search"
            className="timeline-toolbar__search"
            placeholder="Search headlines, descriptions, quotes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label className="timeline-toolbar__date">
            <span>From</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="timeline-toolbar__date">
            <span>To</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          {(allDocs.length > 0 || hasChatSaved) && (
            <label className="timeline-toolbar__date timeline-toolbar__doc">
              <span>Source</span>
              <select
                value={selectedDoc === "all" ? "all" : String(selectedDoc)}
                onChange={(e) =>
                  setSelectedDoc(e.target.value === "all" ? "all" : Number(e.target.value))
                }
                title="Show only events from one source document"
              >
                <option value="all">All sources</option>
                {allDocs.map((d) => (
                  <option key={d.id} value={String(d.id)}>{d.name}</option>
                ))}
                {hasChatSaved && <option value={String(NO_DOC)}>Chat-saved (no document)</option>}
              </select>
            </label>
          )}
          <div className="timeline-toolbar__spacer" />
          <div className="seg-toggle" role="tablist" aria-label="Timeline view mode">
            <button
              type="button"
              role="tab"
              aria-selected={view === "list"}
              className={`seg-toggle__btn ${view === "list" ? "seg-toggle__btn--on" : ""}`}
              onClick={() => setView("list")}
              title="View as a vertical list"
            >
              List
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === "timeline"}
              className={`seg-toggle__btn ${view === "timeline" ? "seg-toggle__btn--on" : ""}`}
              onClick={() => setView("timeline")}
              title="View as a chronological timeline"
            >
              Timeline
            </button>
          </div>
          <button className="btn btn--ghost btn--small" onClick={exportCsv} title="Download filtered events as CSV">
            Export CSV
          </button>
          <button className="btn btn--small" onClick={exportPdf} title="Open print dialog — choose 'Save as PDF'">
            Export PDF
          </button>
        </div>

        <div className="timeline-toolbar__row timeline-toolbar__row--chips">
          <span className="timeline-toolbar__label">Type:</span>
          {CLASSIFICATIONS.map((c) => {
            const on = selectedClasses.has(c.value);
            return (
              <button
                key={c.value}
                type="button"
                className={`chip chip--toggle ${on ? "chip--on" : ""}`}
                onClick={() => toggleSet(selectedClasses, c.value, setSelectedClasses)}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {allActors.length > 0 && (
          <div className="timeline-toolbar__row timeline-toolbar__row--chips">
            <span className="timeline-toolbar__label">Actors:</span>
            <ActorPicker
              all={allActors}
              selected={selectedActors}
              onChange={setSelectedActors}
            />
          </div>
        )}

        <div className="timeline-toolbar__status">
          <span className="muted">
            {total === 0
              ? `Showing 0 of ${events.length} events`
              : `Showing ${pageStartHuman}–${pageEndHuman} of ${total}${
                  filtersActive ? ` (filtered from ${events.length})` : ""
                }`}
          </span>
          {filtersActive && (
            <button className="btn btn--ghost btn--small" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="timeline-print-header print-only">
        <h2>Case Timeline</h2>
        <div className="muted">
          {filtered.length} of {events.length} events
          {(from || to) && ` · ${from || "…"} → ${to || "…"}`}
          {selectedClasses.size > 0 && ` · types: ${[...selectedClasses].map((c) => c.toLowerCase().replace("_", " ")).join(", ")}`}
          {selectedActors.size > 0 && ` · actors: ${[...selectedActors].join(", ")}`}
          {search && ` · search: "${search}"`}
        </div>
      </div>

      <div className="timeline-page__list">
        {total === 0 ? (
          <div className="empty">
            <p>No events match these filters.</p>
            <button className="btn btn--ghost btn--small" onClick={clearFilters}>Clear filters</button>
          </div>
        ) : (
          <>
            {view === "list" ? (
              <ol className="timeline">
                {displayed.map((e) => (
                  <TimelineRow
                    key={e.id}
                    event={e}
                    onOpen={() => setActiveEvent(e)}
                  />
                ))}
              </ol>
            ) : (
              <TimelineChronoView
                events={displayed}
                onOpen={(e) => setActiveEvent(e)}
              />
            )}

            <div className="timeline-pagination no-print">
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={!hasPrev}
                onClick={() => setOffset((o) => Math.max(0, o - pageSize))}
              >
                ‹ Prev
              </button>
              <span className="muted timeline-pagination__status">
                Page {pageNum} of {pageCount}
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={!hasNext}
                onClick={() => setOffset((o) => o + pageSize)}
              >
                Next ›
              </button>
              <div className="timeline-pagination__spacer" />
              <label className="timeline-pagination__size">
                <span className="muted">Per page</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
          </>
        )}
      </div>

      {activeEvent && (
        <EventModal
          event={activeEvent}
          caseId={caseId}
          onClose={() => setActiveEvent(null)}
        />
      )}
    </div>
  );
}

function TimelineRow({
  event,
  onOpen,
}: {
  event: CanonicalEvent;
  onOpen: () => void;
}) {
  const e = event;
  const dateView = eventDateView(e);
  return (
    <li className="timeline__item">
      <div
        className="timeline__row"
        onClick={onOpen}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        title="View source and provenance"
        onKeyDown={(ev) => {
          if (ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            onOpen();
          }
        }}
      >
        <div className="timeline__date">
          <time
            className={dateView.approximate ? "timeline__date--approx" : undefined}
            title={dateView.tooltip}
          >
            {dateView.label}
          </time>
          {dateView.approximate && (
            <span className="timeline__date-tag" title={dateView.tooltip}>
              approx · upload
            </span>
          )}
        </div>
        <div className="timeline__body">
          <div className="timeline__head">
            <strong>{e.headline}</strong>
            <span className={`badge badge--${e.classification.toLowerCase()}`}>
              {e.classification.replace("_", " ").toLowerCase()}
            </span>
          </div>
          {e.actors && e.actors.length > 0 && (
            <div className="timeline__actors">
              {e.actors.map((a, i) => (
                <span key={i} className="chip">{a.name}{a.role ? ` · ${a.role}` : ""}</span>
              ))}
            </div>
          )}
          <div className="timeline__source muted">
            {e.document_id != null
              ? (e.document_name || `Document ${e.document_id}`)
              : "Chat-saved"}
          </div>
        </div>
      </div>

      {/* Print-only detail block — hidden on screen, expanded into the printed
          PDF so the chronology travels with full context and provenance. */}
      <div className="timeline__detail print-only">
        {e.description && <p className="timeline__desc">{e.description}</p>}
        {e.source_span && (
          <blockquote className="timeline__quote">"{e.source_span}"</blockquote>
        )}
        <div className="timeline__prov-print">
          <span>Source: {parseTokenLabel(e.provenance_token)}</span>
          <span className="timeline__prov-token">{e.provenance_token}</span>
        </div>
      </div>
    </li>
  );
}

// ---------- chronological "timeline" visual view ----------

interface ChronoBucket {
  /** Group key for sorting/uniqueness — YYYY for "year" group, "undated" otherwise. */
  key: string;
  /** Year label like "2024" or "Undated". */
  year: string;
  /** Sub-buckets keyed by month (e.g. "January"), in chronological order. */
  months: { month: string; events: CanonicalEvent[] }[];
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function bucketByYearMonth(events: CanonicalEvent[]): ChronoBucket[] {
  // Build a Map<year, Map<monthIdx, events[]>> so we can sort deterministically
  // regardless of incoming order, then collapse it down for rendering.
  const yearMap = new Map<string, Map<number, CanonicalEvent[]>>();
  const undatedKey = "__undated__";

  for (const e of events) {
    const dv = eventDateView(e);
    let yearKey: string;
    let monthIdx: number;
    if (!dv.iso) {
      yearKey = undatedKey;
      monthIdx = 13; // sort to the end
    } else {
      const [yStr, mStr] = dv.iso.split("-");
      yearKey = yStr;
      monthIdx = Math.max(1, Math.min(12, Number(mStr) || 1));
    }
    let months = yearMap.get(yearKey);
    if (!months) {
      months = new Map();
      yearMap.set(yearKey, months);
    }
    const bucket = months.get(monthIdx);
    if (bucket) bucket.push(e);
    else months.set(monthIdx, [e]);
  }

  const sortedYears = Array.from(yearMap.keys()).sort((a, b) => {
    if (a === undatedKey) return 1;
    if (b === undatedKey) return -1;
    return Number(a) - Number(b);
  });

  return sortedYears.map<ChronoBucket>((yk) => {
    const months = yearMap.get(yk)!;
    const sortedMonths = Array.from(months.entries()).sort(([a], [b]) => a - b);
    return {
      key: yk,
      year: yk === undatedKey ? "Undated" : yk,
      months: sortedMonths.map(([mIdx, evs]) => ({
        month: yk === undatedKey ? "" : (MONTH_NAMES[mIdx - 1] ?? ""),
        events: evs,
      })),
    };
  });
}

function TimelineChronoView({
  events,
  onOpen,
}: {
  events: CanonicalEvent[];
  onOpen: (e: CanonicalEvent) => void;
}) {
  const buckets = useMemo(() => bucketByYearMonth(events), [events]);

  return (
    <div className="chrono">
      <div className="chrono__rail" aria-hidden="true" />
      {buckets.map((bucket) => (
        <section key={bucket.key} className="chrono__year">
          <div className="chrono__year-label">
            <span className="chrono__year-pill">{bucket.year}</span>
          </div>
          {bucket.months.map((m, mi) => (
            <div key={`${bucket.key}-${mi}`} className="chrono__month">
              {m.month && <div className="chrono__month-label">{m.month}</div>}
              <ul className="chrono__events">
                {m.events.map((e) => {
                  const dv = eventDateView(e);
                  return (
                    <li key={e.id} className="chrono__event">
                      <span
                        className={`chrono__dot chrono__dot--${e.classification.toLowerCase()}${
                          dv.approximate ? " chrono__dot--approx" : ""
                        }`}
                        aria-hidden="true"
                      />
                      <button
                        type="button"
                        className="chrono__card"
                        onClick={() => onOpen(e)}
                        title="View source and provenance"
                      >
                        <div className="chrono__card-head">
                          <time
                            className={`chrono__card-date${dv.approximate ? " chrono__card-date--approx" : ""}`}
                            title={dv.tooltip}
                          >
                            {dv.label}
                            {dv.approximate && (
                              <span className="chrono__approx-tag"> approx</span>
                            )}
                          </time>
                          <span className={`badge badge--${e.classification.toLowerCase()}`}>
                            {e.classification.replace("_", " ").toLowerCase()}
                          </span>
                        </div>
                        <div className="chrono__card-headline">{e.headline}</div>
                        {e.actors && e.actors.length > 0 && (
                          <div className="chrono__card-actors">
                            {e.actors.slice(0, 4).map((a, i) => (
                              <span key={i} className="chip">
                                {a.name}
                                {a.role ? ` · ${a.role}` : ""}
                              </span>
                            ))}
                            {e.actors.length > 4 && (
                              <span className="chip chrono__more">
                                +{e.actors.length - 4}
                              </span>
                            )}
                          </div>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

// ---------- modal ----------

function EventModal({
  event,
  caseId,
  onClose,
}: {
  event: CanonicalEvent;
  caseId: number;
  onClose: () => void;
}) {
  const [chunk, setChunk] = useState<ChunkResolution | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [showFull, setShowFull] = useState(false);

  // Esc-to-close + body scroll lock while modal is open.
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Resolve provenance whenever the event changes.
  useEffect(() => {
    let cancel = false;
    setChunk(null); setShowFull(false); setErr(null);
    setLoading(true);
    (async () => {
      try {
        const data = await api.get<ChunkResolution>(
          `/cases/${caseId}/chunks/by-token/${encodeURIComponent(event.provenance_token)}`,
        );
        if (!cancel) setChunk(data);
      } catch (e: any) {
        if (!cancel) setErr(e?.message ?? "Could not resolve provenance");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [caseId, event.provenance_token]);

  async function openSource() {
    if (!chunk) return;
    setOpening(true);
    setErr(null);
    try {
      if (isVideo) {
        // Video/audio evidence: open the clip player seeked to the cited
        // moment instead of trying to stream a (non-existent) document.
        const v = await api.get<{ play_url?: string | null }>(
          `/cases/${caseId}/videos/${chunk.video_id}`,
        );
        if (!v.play_url) throw new Error("Source media is not available to play.");
        const at = Math.max(0, Math.floor(chunk.start_seconds ?? 0));
        window.open(`${mediaSrc(v.play_url)}#t=${at}`, "_blank", "noopener");
        return;
      }
      const blob = await api.blob(`/cases/${caseId}/documents/${chunk.document_id}/raw`);
      const url = URL.createObjectURL(blob);
      const target = chunk.page ? `${url}#page=${chunk.page}` : url;
      window.open(target, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      setErr(e?.message ?? "Could not open source file");
    } finally {
      setOpening(false);
    }
  }

  const parsed = parseToken(event.provenance_token);
  const isVideo = chunk?.source_type === "video";
  const doc = chunk?.document_name ?? `Document ${parsed.documentId ?? "?"}`;
  const page = isVideo ? null : chunk?.page ?? parsed.page ?? null;
  const para = isVideo ? null : chunk?.paragraph ?? parsed.paragraph ?? null;
  const clockAt =
    isVideo && chunk?.start_seconds != null ? formatClock(chunk.start_seconds) : null;
  const isDeleted = chunk?.document_deleted === true;
  const openLabel = isDeleted
    ? isVideo
      ? "Media removed"
      : "Document removed"
    : opening
      ? "Opening…"
      : isVideo
        ? "Open source clip ↗"
        : "Open source document ↗";

  return (
    <div
      className="modal no-print"
      role="dialog"
      aria-modal="true"
      aria-labelledby="event-modal-title"
      onClick={onClose}
    >
      <div
        className="modal__card modal__card--lg modal__card--split"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="modal__header">
          <div className="modal__header-main">
            <div className="modal__header-meta">
              {(() => {
                const dv = eventDateView(event);
                return (
                  <time
                    className={`modal__header-date${dv.approximate ? " modal__header-date--approx" : ""}`}
                    title={dv.tooltip}
                  >
                    {dv.label}
                    {dv.approximate && <span className="timeline__date-tag"> approx · upload</span>}
                  </time>
                );
              })()}
              <span className={`badge badge--${event.classification.toLowerCase()}`}>
                {event.classification.replace("_", " ").toLowerCase()}
              </span>
            </div>
            <h2 id="event-modal-title" className="modal__header-title">{event.headline}</h2>
          </div>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
          >
            ×
          </button>
        </header>

        {isDeleted && (
          <div className="alert alert--warning modal__deleted-banner" role="note">
            This {isVideo ? "media file" : "document"} has been removed from the case. The source text below is preserved for reference, but the original file can no longer be opened.
          </div>
        )}

        <div className="modal__body">
          {event.description && <p className="modal__desc">{event.description}</p>}

          {event.actors && event.actors.length > 0 && (
            <div className="modal__section">
              <div className="modal__section-label">Actors</div>
              <div className="timeline__actors">
                {event.actors.map((a, i) => (
                  <span key={i} className="chip">{a.name}{a.role ? ` · ${a.role}` : ""}</span>
                ))}
              </div>
            </div>
          )}

          {event.source_span && (
            <div className="modal__section">
              <div className="modal__section-label">Source quote</div>
              <blockquote className="timeline__quote">"{event.source_span}"</blockquote>
            </div>
          )}

          <div className="modal__section">
            <div className="modal__section-label">Provenance</div>
            <div className="timeline__prov">
              <div className="timeline__prov-meta">
                <span className="timeline__prov-doc">{doc}</span>
                {page != null && <span className="muted">· page {page}</span>}
                {para != null && <span className="muted">· ¶{para}</span>}
                {clockAt != null && <span className="muted">· @ {clockAt}</span>}
                {isVideo && chunk?.speaker_label && (
                  <span className="muted">· {chunk.speaker_label}</span>
                )}
              </div>
              <div className="timeline__prov-token">{event.provenance_token}</div>
              {loading && <div className="muted timeline__prov-status">Resolving source…</div>}
              {err && <div className="alert alert--error">{err}</div>}
              {chunk && (
                <div className="timeline__prov-actions">
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => setShowFull((v) => !v)}
                  >
                    {showFull ? "Hide full chunk" : "Show full chunk"}
                  </button>
                </div>
              )}
              {showFull && chunk && (
                <pre className="timeline__prov-text">{chunk.text}</pre>
              )}
            </div>
          </div>
        </div>

        <footer className="modal__footer">
          <span className="muted modal__footer-hint">
            {chunk
              ? `${doc}${clockAt ? ` · @ ${clockAt}` : ""}${page ? ` · p.${page}` : ""}${para ? ` · ¶${para}` : ""}`
              : loading ? "Resolving source…" : ""}
          </span>
          <div className="modal__footer-actions">
            <button type="button" className="btn" onClick={onClose}>Close</button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={openSource}
              disabled={isDeleted || !chunk || opening}
              title={
                isDeleted
                  ? isVideo ? "Source media has been removed" : "Source document has been removed"
                  : chunk
                    ? isVideo
                      ? `Play ${doc}${clockAt ? ` at ${clockAt}` : ""}`
                      : `Open ${doc}${page ? ` at page ${page}` : ""}`
                    : "Resolving source…"
              }
            >
              {openLabel}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ---------- actor picker ----------

function ActorPicker({
  all,
  selected,
  onChange,
}: {
  all: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const suggestions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const out: string[] = [];
    for (const name of all) {
      if (selected.has(name)) continue;
      if (!needle || name.toLowerCase().includes(needle)) out.push(name);
      if (out.length >= 50) break;
    }
    return out;
  }, [all, selected, query]);

  // Reset highlight when the suggestion list changes shape.
  useEffect(() => { setHighlight(0); }, [query, selected]);

  // Close the popover when focus/click moves outside the picker.
  useEffect(() => {
    if (!open) return;
    function onDoc(ev: MouseEvent) {
      if (!wrapRef.current?.contains(ev.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function add(name: string) {
    const next = new Set(selected);
    next.add(name);
    onChange(next);
    setQuery("");
    setOpen(true);
    inputRef.current?.focus();
  }

  function remove(name: string) {
    const next = new Set(selected);
    next.delete(name);
    onChange(next);
  }

  function onKeyDown(ev: React.KeyboardEvent<HTMLInputElement>) {
    if (ev.key === "ArrowDown") {
      ev.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(0, suggestions.length - 1)));
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (ev.key === "Enter") {
      if (suggestions[highlight]) {
        ev.preventDefault();
        add(suggestions[highlight]);
      }
    } else if (ev.key === "Escape") {
      setOpen(false);
    } else if (ev.key === "Backspace" && !query && selected.size > 0) {
      // Delete the most recently added chip when the input is empty.
      const last = Array.from(selected).pop();
      if (last) remove(last);
    }
  }

  const selectedList = Array.from(selected);

  return (
    <div className="actor-picker" ref={wrapRef}>
      {selectedList.map((name) => (
        <span key={name} className="chip chip--on actor-picker__chip">
          {name}
          <button
            type="button"
            className="actor-picker__chip-x"
            aria-label={`Remove ${name}`}
            onClick={() => remove(name)}
          >
            ×
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        className="actor-picker__input"
        placeholder={selected.size === 0 ? "Filter by actor…" : "Add actor…"}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && suggestions.length > 0 && (
        <ul className="actor-picker__menu" role="listbox">
          {suggestions.map((name, i) => (
            <li
              key={name}
              role="option"
              aria-selected={i === highlight}
              className={`actor-picker__opt ${i === highlight ? "actor-picker__opt--hl" : ""}`}
              onMouseDown={(ev) => { ev.preventDefault(); add(name); }}
              onMouseEnter={() => setHighlight(i)}
            >
              {name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- helpers ----------

/** Pretty-formatted date string for display in the row. Returns "(undated)"
 *  only when neither the event nor its source document has any date — i.e.
 *  almost never in practice. */
function formatDate(d: string | null, dt: string | null, precision: string): string {
  const raw = dt ?? d;
  if (!raw) return "(undated)";
  const date = new Date(raw);
  if (Number.isNaN(+date)) return raw;
  switch (precision) {
    case "year":     return date.toLocaleDateString(undefined, { year: "numeric" });
    case "month":    return date.toLocaleDateString(undefined, { year: "numeric", month: "long" });
    case "datetime": return date.toLocaleString();
    default:         return date.toLocaleDateString();
  }
}

/** What the row should *show* for its date. When the event itself has no
 *  date the LLM could pin down, we fall back to the source document's upload
 *  date and tag the result as approximate so the lawyer can see at a glance
 *  that this row is anchored to the document, not to a real event date. */
interface EventDateView {
  /** Human-readable label for the row's "date" column. */
  label: string;
  /** ISO YYYY-MM-DD string we should sort/group by, or null if truly undated. */
  iso: string | null;
  /** True iff we fell back to the document upload date. */
  approximate: boolean;
  /** Tooltip explaining the source of the date. */
  tooltip: string;
}

function eventDateView(e: CanonicalEvent): EventDateView {
  const raw = e.event_datetime ?? e.event_date;
  if (raw) {
    const iso = raw.slice(0, 10);
    return {
      label: formatDate(e.event_date, e.event_datetime, e.date_precision),
      iso,
      approximate: false,
      tooltip: `Event date extracted from source · precision: ${e.date_precision}`,
    };
  }
  if (e.document_uploaded_at) {
    const date = new Date(e.document_uploaded_at);
    if (!Number.isNaN(+date)) {
      const iso = e.document_uploaded_at.slice(0, 10);
      const docName = e.document_name ? `"${e.document_name}"` : "the source document";
      return {
        label: date.toLocaleDateString(),
        iso,
        approximate: true,
        tooltip: `No date in source — using upload date of ${docName}`,
      };
    }
  }
  return { label: "(undated)", iso: null, approximate: false, tooltip: "No date available" };
}

function csvCell(v: string): string {
  if (/[",\r\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

function todayStamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

// Same convention as ChatPane/VideoPlayer: play_url paths come back as
// "/api/..."; rebase onto the configured API base so a bare URL opens in dev
// (absolute VITE_API_BASE) and prod (same-origin "/api").
const MEDIA_BASE = (import.meta as any).env?.VITE_API_BASE ?? "/api";

function mediaSrc(apiPath: string): string {
  return `${MEDIA_BASE}${apiPath.replace(/^\/api/, "")}`;
}

function formatClock(seconds: number | null | undefined): string {
  if (seconds == null || !isFinite(seconds)) return "0:00";
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${m}:${String(sec).padStart(2, "0")}`;
}

function parseToken(token: string): {
  documentId: number | null;
  page: number | null;
  paragraph: number | null;
} {
  // PT-D{doc}-P{page}-par{paragraph}[-c{idx}]-{hash8}
  const m = token.match(/^PT-D(\d+)-P(\d+)-par(\d+)/);
  if (!m) return { documentId: null, page: null, paragraph: null };
  return {
    documentId: Number(m[1]),
    page: Number(m[2]) || null,
    paragraph: Number(m[3]) || null,
  };
}

function parseTokenLabel(token: string): string {
  const p = parseToken(token);
  if (p.documentId == null) return token;
  const bits = [`Document ${p.documentId}`];
  if (p.page) bits.push(`page ${p.page}`);
  if (p.paragraph) bits.push(`¶${p.paragraph}`);
  return bits.join(" · ");
}
